import { createFileRoute, useNavigate, Link as RouterLink } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import { Handshake } from '@mui/icons-material';
import * as enablementApi from '@/api/enablement';
import { LoadingScreen } from '@/components/LoadingScreen';
import { EmptyState } from '@/components/EmptyState';
import { EnablementStateChip } from '@/components/EnablementStateChip';
import { EnablementTurnBadge } from '@/components/EnablementTurnBadge';
import { formatDateTime } from '@/lib/formatters';

const LinkAny = RouterLink as unknown as React.ElementType;

export const Route = createFileRoute('/_authenticated/enablement-rooms')({
  component: EnablementRoomsPage,
});

/**
 * Bandeja de habilitaciones (Slice 6 — §6.5): todas las salas en curso del tenant con su estado y
 * "de quién es el turno". El panel de control del contador para onboardear clientes.
 */
function EnablementRoomsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ['enablement-rooms'],
    queryFn: () => enablementApi.listRooms(),
  });

  if (isLoading) return <LoadingScreen />;

  const rooms = data ?? [];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Activación AFIP</Typography>
        <Typography variant="body2" color="text.secondary">
          Salas de habilitación de certificados en curso y de quién es el turno en cada una.
        </Typography>
      </Box>

      {rooms.length === 0 ? (
        <EmptyState
          icon={<Handshake sx={{ fontSize: 56 }} />}
          title="No hay habilitaciones en curso"
          description="Abrí una sala desde el detalle de un CUIT para coordinar el trámite del certificado AFIP con tu contador o developer."
          actionLabel="Ir a CUITs"
          onAction={() => navigate({ to: '/cuit-profiles' })}
        />
      ) : (
        <Stack spacing={2}>
          {rooms.map((room) => (
            <Card key={room.id} variant="outlined">
              <CardActionArea
                component={LinkAny}
                to="/cuit-profiles/$id/enablement"
                params={{ id: room.cuitProfileId }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ sm: 'center' }}
                    spacing={1.5}
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        CUIT {room.cuitProfileId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Actualizada {formatDateTime(room.updatedAt)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <EnablementStateChip state={room.state} />
                      <EnablementTurnBadge turn={room.currentTurn} />
                    </Stack>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
